/* eslint-disable eslint-comments/disable-enable-pair -- needed */
/* eslint-disable jsx-a11y/no-static-element-interactions  -- needed  */
/* eslint-disable jsx-a11y/click-events-have-key-events  -- needed */
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  string,
  shape,
  func,
} from 'prop-types';
import {
  ButtonBase,
  Heading,
  Layout,
  Link,
  ProgressCircular,
} from '@americanexpress/dls-react';
import { useReactlytics } from '@americanexpress/reactlytics';
import { DualTracker, TrackerProgressTile } from '@americanexpress/axp-benefits-utils/components';
import { useGetBestTrackers } from '@americanexpress/axp-benefits-utils/hooks';
import { isMultiTracker } from '@americanexpress/axp-benefits-utils/utils';
import useVisibilityTracker from './useVisibilityTracker';
import {
  fetchBenefitObject,
  getBenefitNamesListFromAllBenefits,
  getBenefitsDataForQuickView,
  getTrackersDataForDisplay,
  isWalmartBenefitId,
} from '../utils/utils';
import { trackProgress, viewTerms, MODULE_NAME } from '../constants';
import styles from './AxpBenefitsActivity.scss';
import BenefitCards from './BenefitCards';
import { navigateIfMobile } from '../utils/mobileNavigationHelper';
import { AIRLINE_BONUS_BENEFIT_NAME } from '../utils/constants';

const TrackBenefits = (props) => {
  const [selectedSorBenefitId, setSelectedSorBenefitId] = useState(null);
  const [selectedBenefitName, setSelectedBenefitName] = useState(null);
  const [showQuickView, setShowQuickView] = useState(true);
  const toggleModal = () => setShowQuickView(!showQuickView);

  const { track } = useReactlytics();
  const intl = useIntl();
  const {
    cardName,
    viewAllBenefits,
    languageData,
    locale,
    renderQuickView,
    benefitsForActivity,
  } = props;
  const { data, isLoading } = useGetBestTrackers({
    limit: 'ALL',
    locale: intl.locale,
  }, { moduleId: MODULE_NAME });

  const { spendProgress, creditEarned, completedBenefits } = getTrackersDataForDisplay(data?.body);
  const trackers = data?.body?.[0]?.trackers;

  const handleOpenQuickView = useCallback((sorBenefitId, benefitName) => {
    const benefit = Object.values(viewAllBenefits).find(
      (b) => b.sorBenefitId === sorBenefitId && b.benefitName === benefitName
    );
    if (benefit && navigateIfMobile(benefit)) return;
    setSelectedSorBenefitId(sorBenefitId);
    setSelectedBenefitName(benefitName);
    setShowQuickView(true);
  }, [viewAllBenefits]);

  // TODO: Temporary workaround — airline-bonus shares a sorBenefitId with
  // another benefit. Exclude it so the correct benefit opens from tracker tiles.
  const handleOpenTrackerQuickView = useCallback((sorBenefitId) => {
    const benefit = Object.values(viewAllBenefits).find(
      (b) => b.sorBenefitId === sorBenefitId && b.benefitName !== AIRLINE_BONUS_BENEFIT_NAME
    );
    if (benefit && navigateIfMobile(benefit)) return;
    setSelectedSorBenefitId(sorBenefitId);
    setSelectedBenefitName(benefit?.benefitName || null);
    setShowQuickView(true);
  }, [viewAllBenefits]);

  const trackerSorBenefitIds = new Set(trackers?.map((tracker) => tracker.sorBenefitId));
  const enrolledBenefits = Object.values(benefitsForActivity).filter(
    (benefit) => benefit?.enrollmentApplication?.enrollmentStatus === 'ENROLLED'
    && benefit?.sorBenefitId
    // TODO: Temporary workaround — airline-bonus shares a sorBenefitId with
    // another benefit's tracker. Allow it through even though its ID is in the tracker set.
    && (!trackerSorBenefitIds.has(benefit.sorBenefitId)
      || benefit?.benefitName === AIRLINE_BONUS_BENEFIT_NAME)
    && benefit?.enrollmentOriginType !== 'EXTERNAL'
  );

  const renderEnrolledNonTrackerBenefits = () => {
    if (enrolledBenefits.length === 0) return null;
    return (
      enrolledBenefits.map((benefit) => (
        <div key={benefit?.sorBenefitId} className="width-full flex height-full">
          <BenefitCards
            data-testid={`benefit-card-click-enrolled-${benefit?.sorBenefitId}`}
            benefit={benefit}
            onBenefitClick={() => handleOpenQuickView(benefit?.sorBenefitId, benefit?.benefitName)}
          />
        </div>
      ))
    );
  };

  const renderBenefitTrackers = (sectionType, benefitTrackersData) => benefitTrackersData
    .map((trackerData, index) => {
      const shouldRenderDualTracker = isMultiTracker(trackerData);
      const sorBenefitId = shouldRenderDualTracker
        ? trackerData?.[0]?.sorBenefitId : trackerData?.sorBenefitId;
      return shouldRenderDualTracker ? (
        <div
          data-testid={`${sectionType}-${index}-${sorBenefitId}`}
          key={sorBenefitId}
          className="width-full flex height-full"
          onClick={() => handleOpenTrackerQuickView(sorBenefitId)}
        >
          <DualTracker
            trackers={trackerData}
            isCurrency={trackerData?.[0]?.tracker?.targetUnit === 'MONETARY' || trackerData?.[1]?.tracker?.targetUnit === 'MONETARY'}
            savingsLabel={intl.formatMessage({
              id: 'earned-this-year',
              defaultMessage: 'Earned this Year',
            })}
          />
        </div>
      ) : (
        <ButtonBase
          data-testid={`${sectionType}-${index}-${sorBenefitId}`}
          key={sorBenefitId}
          className="width-full flex height-full"
          aria-label={trackerData?.benefitName}
          onClick={() => handleOpenTrackerQuickView(sorBenefitId)}
        >
          <TrackerProgressTile
            status={trackerData?.status}
            benefitId={trackerData?.benefitId}
            benefitName={trackerData?.benefitName}
            sorBenefitId={sorBenefitId}
            category={trackerData?.category}
            trackerDuration={trackerData?.trackerDuration}
            targetAmount={trackerData?.tracker?.targetAmount}
            spentAmount={trackerData?.tracker?.spentAmount}
            remainingAmount={trackerData?.tracker?.remainingAmount}
            targetCurrency={trackerData?.tracker?.targetCurrency}
            targetCurrencySymbol={trackerData?.tracker?.targetCurrencySymbol}
            targetUnit={trackerData?.tracker?.targetUnit}
            title={trackerData?.progress?.title}
            showTitle={true}
            message={trackerData?.progress?.message}
            hideProgressBar={trackerData?.progress?.hideProgressBar}
            isLabelBeforeAmount={trackerData?.progress?.isLabelBeforeAmount}
            usedLabel={trackerData?.progress?.usedLabel}
            togoLabel={trackerData?.progress?.togoLabel}
            savingsLabel={intl.formatMessage({ id: 'earned-this-year', defaultMessage: 'Earned this Year' })}
            totalSavingsYearToDate={trackerData?.progress?.totalSavingsYearToDate}
            updateInterval={trackerData?.progress?.updateInterval}
            showUpdateInterval={false}
            progressBarIcon={trackerData?.category === 'usage' ? 'IconRewards' : 'IconCardBenefit'}
            showWalmartInProgressLayout={isWalmartBenefitId(sorBenefitId)}
            showMultiLevel={trackerData?.multiLevel}
            multiLevelData={trackerData?.levels}
            periodStartDate={trackerData?.periodStartDate}
            periodEndDate={trackerData?.periodEndDate}
          />
        </ButtonBase>
      );
    });

  const GetTrackerRef = (trackerLabel) => useVisibilityTracker(trackerLabel, (label) => {
    track({ event: { element: `${cardName}>>${label}`, type: 'impression' } });
  });

  const trackerComponentRef = GetTrackerRef('tracker-component');
  const spendTrackerRef = GetTrackerRef('spend-tracker');
  const creditTrackerRef = GetTrackerRef('credit-tracker');
  const completedTrackerRef = GetTrackerRef('completed-tracker');

  if (isLoading || !data?.body) {
    return (
      <ProgressCircular data-testid="progress-circular" />
    );
  }

  return (
    <Layout className="margin-2-t margin-1-b">
      <div
        className="margin-responsive-t margin-3-lr margin-3-b"
        ref={trackerComponentRef}
        data-testid="track-benefits-heading-section"
      >
        <Heading
          id="track-benefits-heading"
          data-testid="track-benefits-heading"
          level={2}
          variant="sans-medium-bold"
          className="color-text-brand-alt pad-6 margin-1-b"
        >
          {trackProgress}
        </Heading>
        <span
          id="track-benefits-subheading"
          data-testid="track-benefits-subheading"
          className="body color-text-regular"
        >
          {intl.formatMessage({ id: 'tracker-agnostic-disclaimer', defaultMessage: 'Trackers may not reflect recent activity. Benefits/bonuses may not be received or may be reversed if purchases are cancelled or refunded.' })}
          {' '}
          <Link
            id="track-benefits-view-term-cta"
            data-testid="track-benefits-view-term-cta"
            href="/card-benefits/terms/platinum#top"
            isInline={true}
          >
            {viewTerms}
          </Link>
          .
        </span>
      </div>

      <div ref={spendTrackerRef} className="margin-3-lr">
        <Heading
          level={2}
          variant="sans-small-bold"
          className="color-text-regular margin-3-t margin-3-b"
        >
          <span>{`Spend Progress (${spendProgress.length})`}</span>
        </Heading>
        <div className={styles.cardContainer}>
          {renderBenefitTrackers('spend-benefits', spendProgress)}
        </div>
      </div>

      <div ref={creditTrackerRef} className="margin-3-lr">
        <Heading
          level={2}
          variant="sans-small-bold"
          className="color-text-regular margin-3-t margin-3-b"
        >
          <span>{`Credit Earned (${creditEarned.length})`}</span>
        </Heading>
        <div className={styles.cardContainer}>
          {renderBenefitTrackers('credit-earned', creditEarned)}
        </div>
      </div>

      <div ref={completedTrackerRef} className="margin-3-lr margin-3-b">
        <Heading
          level={2}
          variant="sans-small-bold"
          className="color-text-regular margin-3-t margin-3-b"
        >
          <span>{`Completed Benefits (${completedBenefits.length + enrolledBenefits.length})`}</span>
        </Heading>
        <div className={styles.cardContainer}>
          {renderBenefitTrackers('completed-benefits', completedBenefits)}
          {renderEnrolledNonTrackerBenefits()}
        </div>
      </div>
      {(() => {
        if (!selectedSorBenefitId || !showQuickView) return null;

        const selectedBenefit = selectedBenefitName
          ? Object.values(viewAllBenefits).find(
            (b) => b.benefitName === selectedBenefitName
          ) || fetchBenefitObject(viewAllBenefits, selectedSorBenefitId)
          : fetchBenefitObject(viewAllBenefits, selectedSorBenefitId);

        const trackerBenefitsDataForQuickView = getBenefitsDataForQuickView(
          viewAllBenefits,
          trackers
        );
        const benefitNameForQuickView = selectedBenefit?.benefitName;
        const benefitsDataForQuickView = benefitNameForQuickView
          && !trackerBenefitsDataForQuickView?.[benefitNameForQuickView]
          ? {
            ...trackerBenefitsDataForQuickView,
            [benefitNameForQuickView]: selectedBenefit,
          }
          : trackerBenefitsDataForQuickView;

        return renderQuickView({
          benefit: selectedBenefit,
          benefitsDataForQuickView,
          benefitNamesListFromActivity: getBenefitNamesListFromAllBenefits(
            benefitsDataForQuickView
          ),
          languageData,
          locale,
          cardName,
          toggleModal,
          trackerData: data?.body,
        });
      })()}
    </Layout>
  );
};

export default TrackBenefits;

TrackBenefits.propTypes = {
  cardName: string.isRequired,
  viewAllBenefits: shape({}).isRequired,
  languageData: shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: string.isRequired,
  renderQuickView: func.isRequired,
  benefitsForActivity: shape({}).isRequired,
};
